/**
 * FRONT-1250: one-line descriptions rendered their Markdown source verbatim.
 *
 * Inbox row subtitles, the Files card/detail description and similar compact
 * summaries are written by Eddy (and by the skills it runs), which means they
 * routinely carry Markdown — `**bold**`, `## Heading`, `- bullets`. Those
 * surfaces printed the raw string, so the reader saw the asterisks and hashes
 * instead of the formatting.
 *
 * The full `MarkdownRenderer` is the wrong tool for them: it emits block
 * elements (h1..h6, ul, blockquote, pre) with their own margins, which blow a
 * `line-clamp-2` subtitle out of its row. So descriptions get this instead —
 * block syntax is FLATTENED to plain text (a heading becomes its words, a list
 * item becomes a bullet glyph) and only inline formatting is actually rendered.
 *
 * Links and images are reduced to their text: a description is often nested
 * inside a clickable row, where a real anchor would both navigate away and
 * trigger the row's own click, and an `<img>` would break the row's height.
 *
 * SECURITY: the output is fed to `dangerouslySetInnerHTML`, and the input is
 * model-authored — a cron result or file description can quote text Eddy read
 * from the open web. So embedded HTML is ESCAPED, never deleted: deleting is
 * both lossy (a description may legitimately mention `<section>`) and unsafe,
 * because a single deletion pass lets the neighbours of a removed tag re-join
 * into a live one (`<if<x>rame …>` becomes `<iframe …>`). Escaping happens in
 * marked's own inline-HTML renderer, so nothing can be reassembled afterwards.
 *
 * Pure and DOM-free on purpose so the flattening rules are unit-testable
 * without rendering — the component wrapper is `~/components/inline-markdown`.
 */
import { Marked } from 'marked';
import { escapeLoneTildes } from './markdown-tildes';

/** ``` / ~~~ fence lines — the delimiter goes, the code text stays. */
const CODE_FENCE = /^\s*(?:`{3,}|~{3,}).*$/;
/** `---`, `***`, `___` rules, and the `===` / `---` underline of a setext heading. */
const RULE_OR_SETEXT = /^\s*(?:-{3,}|\*{3,}|_{3,}|={2,})\s*$/;
/** A table's separator row: `|---|:--:|`. Its pipes and dashes are not content. */
const TABLE_DIVIDER = /^\s*\|[\s:|-]*-[\s:|-]*\|?\s*$/;

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * A private `marked` instance — never the shared singleton, so overriding the
 * inline-HTML renderer here can't change how chat messages or documents render.
 */
const inlineMarked = new Marked({
  renderer: {
    // Escape embedded HTML rather than passing it through (see SECURITY above).
    html: (token: { raw?: string; text?: string }) => escapeHtml(token.raw ?? token.text ?? ''),
  },
});

/**
 * Reduce block-level Markdown to plain text, leaving inline syntax intact.
 *
 * Headings lose their `#`s, bullets become `•`, and blockquote markers, code
 * fences, rules and table dividers are dropped. Blank lines are dropped too:
 * the compact callers render in a `line-clamp`ped element where newlines
 * already collapse to spaces, so an empty line only ever showed up as a double
 * space — and the two unclamped callers (the Files detail line, an expanded
 * inbox row) don't honour newlines either, for the same CSS reason.
 */
export function flattenMarkdownBlocks(source: string): string {
  const out: string[] = [];
  for (const rawLine of source.split(/\r?\n/)) {
    if (CODE_FENCE.test(rawLine) || RULE_OR_SETEXT.test(rawLine)) continue;
    let line = rawLine.trim();
    if (!line) continue;
    if (TABLE_DIVIDER.test(line)) continue;
    // Blockquote markers can stack ("> > quoted").
    while (/^>\s?/.test(line)) line = line.replace(/^>\s?/, '').trim();
    // ATX heading. The closing hashes only go when the line really opened as a
    // heading — an unconditional strip would eat the tail of "Rating: ###".
    if (/^#{1,6}\s+/.test(line)) {
      line = line.replace(/^#{1,6}\s+/, '').replace(/\s+#+\s*$/, '');
    }
    // Task-list checkbox first, so "- [ ] thing" doesn't keep the brackets.
    line = line.replace(/^[-*+]\s+\[[ xX]\]\s+/, '• ');
    line = line.replace(/^[-*+]\s+/, '• ');
    if (!line) continue;
    out.push(line);
  }
  return out.join('\n');
}

/**
 * Strip `<script>` blocks and inline event handlers — mirrors
 * `MarkdownRenderer`.
 *
 * The block pattern is deliberately a LAZY `[\s\S]*?` rather than the usual
 * tempered-greedy `[^<]*(?:(?!<\/script>)<[^<]*)*`. The tempered form has
 * nested quantifiers, so an unterminated `<script` followed by a run of `<`
 * characters makes it backtrack catastrophically — a description pasted from
 * a document is enough to hang the render. The lazy form scans linearly and
 * matches the same thing.
 */
function sanitize(html: string): string {
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/\bon\w+\s*=/gi, 'data-blocked=');
}

/**
 * Render a short description as inline-only HTML: emphasis, strong, inline
 * code and strikethrough survive; everything block-level is flattened away and
 * anchors/images are reduced to their text (see the module doc for why).
 */
export function inlineMarkdownToHtml(source: string): string {
  const flattened = flattenMarkdownBlocks(source);
  if (!flattened) return '';
  // FRONT-1346: same lone-tilde guard as the full renderer.
  const rendered = inlineMarked.parseInline(escapeLoneTildes(flattened), {
    async: false,
  }) as string;
  return sanitize(
    rendered
      // Anchors are unwrapped to their label. marked has already parsed the
      // link (including a URL with balanced parens) and escaped any embedded
      // HTML, so these patterns only ever match marked's own output.
      .replace(/<a\b[^>]*>/gi, '')
      .replace(/<\/a>/gi, '')
      // An image keeps its alt text; the element itself would stretch the row.
      .replace(/<img\b[^>]*?\salt="([^"]*)"[^>]*>/gi, '$1')
      .replace(/<img\b[^>]*>/gi, ''),
  );
}
